import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Store, User, ShieldCheck, CreditCard, 
  HeadphonesIcon, ChevronLeft, ChevronRight, Menu, X, LogOut,
  BadgeCheck, ClipboardList, Building2, Briefcase
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Sidebar({ isOpen, setIsOpen }) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [counts, setCounts] = useState({
    stores: 0,
    pendingStores: 0,
    rejectedStores: 0,
    payments: 0
  });
  const [isApprovedAgent, setIsApprovedAgent] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsOpen]);

  // Fetch counts for sidebar badges
  useEffect(() => {
    const fetchCounts = async () => {
      const token = localStorage.getItem('business_token');
      if (!token) return;

      try {
        const response = await axios.get(`${BACKEND_URL}/api/stores`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          const stores = response.data;
          setCounts({
            stores: stores.filter(s => s.status === 'active').length,
            pendingStores: stores.filter(s => s.status === 'pending').length,
            rejectedStores: stores.filter(s => s.status === 'rejected').length,
            payments: 0 // Can be fetched separately if needed
          });
        }
      } catch (error) {
        console.error('Error fetching sidebar counts:', error);
      }
    };

    fetchCounts();
  }, [location.pathname]); // Refetch when route changes

  // Check if user is approved agent
  useEffect(() => {
    const checkAgentStatus = async () => {
      const token = localStorage.getItem('business_token');
      if (!token) return;

      try {
        const response = await axios.get(`${BACKEND_URL}/api/agent-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data && response.data.length > 0) {
          const approvedRequest = response.data.find(req => req.status === 'approved');
          setIsApprovedAgent(!!approvedRequest);
        }
      } catch (error) {
        console.error('Error checking agent status:', error);
      }
    };

    checkAgentStatus();
  }, [location.pathname]);

  const menuItems = [
    { 
      id: 'dashboard',
      path: '/dashboard', 
      icon: LayoutDashboard, 
      label: language === 'ar' ? 'لوحة التحكم' : 'Dashboard',
      gradient: 'from-[#38BDF8] to-[#0EA5E9]',
      count: null
    },
    { 
      id: 'account',
      path: '/account', 
      icon: User, 
      label: language === 'ar' ? 'حسابي' : 'My Account',
      gradient: 'from-[#1E9BD9] to-[#0D7AB5]',
      count: null
    },
    { 
      id: 'stores',
      path: '/stores', 
      icon: Store, 
      label: language === 'ar' ? 'متاجري' : 'My Stores',
      gradient: 'from-emerald-500 to-teal-600',
      count: counts.stores > 0 ? counts.stores : null,
      badge: counts.pendingStores > 0 ? { count: counts.pendingStores, color: 'bg-amber-500' } : null
    },
    {
      // iter-250: linked company files from ASB606 admin
      id: 'my-companies',
      path: '/my-companies',
      icon: Building2,
      label: language === 'ar' ? 'شركاتي' : 'My Companies',
      gradient: 'from-blue-500 to-cyan-600',
      count: null,
    },
    { 
      id: 'agent',
      path: isApprovedAgent ? '/agent-tasks' : '/become-agent', 
      icon: isApprovedAgent ? BadgeCheck : ShieldCheck, 
      label: isApprovedAgent 
        ? (language === 'ar' ? 'صفحة الوكيل' : 'Agent Page')
        : (language === 'ar' ? 'وكيل معتمد' : 'Authorized Agent'),
      gradient: isApprovedAgent ? 'from-green-500 to-emerald-500' : 'from-[#F97316] to-amber-500',
      count: null,
      badge: isApprovedAgent ? { icon: '✓', color: 'bg-green-500' } : null
    },
    ...(isApprovedAgent ? [] : []),
    {
      id: 'payments',
      path: '/payments', 
      icon: CreditCard, 
      label: language === 'ar' ? 'الأموال' : 'Money',
      gradient: 'from-indigo-500 to-blue-600',
      count: null
    },
    {
      id: 'service-requests',
      path: '/service-requests',
      icon: Briefcase,
      label: language === 'ar' ? 'طلبات الخدمات' : 'Service Requests',
      gradient: 'from-cyan-500 to-sky-600',
      count: null
    },
    { 
      id: 'support',
      path: '/support', 
      icon: HeadphonesIcon, 
      label: language === 'ar' ? 'الدعم' : 'Support',
      gradient: 'from-red-500 to-red-600',
      count: null,
      badge: { icon: '✨', color: 'bg-purple-500' }
    },
  ];

  const handleLogout = () => {
    // iter-302: smooth logout — navigate to '/' FIRST (before clearing state)
    // so the ProtectedRoute never bounces to /login during the tear-down.
    // Then clear storage and notify App.js to drop the user from state.
    navigate('/', { replace: true });
    localStorage.removeItem('business_token');
    localStorage.removeItem('business_user');
    window.dispatchEvent(new Event('userLoggedOut'));
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    // Handle agent paths
    if (path === '/become-agent' || path === '/agent-tasks') {
      return location.pathname === '/become-agent' || location.pathname === '/agent-tasks';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-16 sm:top-20 bottom-0 start-0 z-40 transition-all duration-300 ${
          isDarkMode 
            ? 'bg-[#0B0F19] border-white/10' 
            : 'bg-white border-black/5'
        } border-e ${
          isMobile 
            ? isOpen 
              ? 'w-64 translate-x-0 rtl:translate-x-0' 
              : 'w-0 -translate-x-full rtl:translate-x-full overflow-hidden'
            : isOpen ? 'w-64' : 'w-20'
        }`}
        style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
        data-testid="sidebar"
      >
        <div className={`h-full flex flex-col ${isOpen ? 'px-3' : 'px-2'} py-6 overflow-hidden`}>
          {/* Toggle Button - Desktop */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`hidden lg:flex items-center justify-center w-full mb-6 p-2.5 rounded-xl transition-all duration-300 ${
              isDarkMode 
                ? 'hover:bg-white/10 text-gray-400 hover:text-white' 
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
            }`}
            data-testid="sidebar-toggle"
          >
            {isOpen ? (
              <>
                {language === 'ar' ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
                <span className={`ms-2 text-sm font-medium ${!isOpen && 'hidden'}`}>
                  {language === 'ar' ? 'إخفاء القائمة' : 'Collapse'}
                </span>
              </>
            ) : (
              language === 'ar' ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />
            )}
          </button>

          {/* Menu Items */}
          <nav className="flex-1 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => isMobile && setIsOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3.5 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                    active
                      ? `bg-gradient-to-r ${item.gradient} text-white shadow-lg`
                      : isDarkMode
                        ? 'text-gray-400 hover:text-white hover:bg-white/10'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  } ${!isOpen && 'lg:justify-center lg:px-0'}`}
                  data-testid={`sidebar-${item.id}`}
                >
                  {/* Gradient glow for active item */}
                  {active && (
                    <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-20 blur-xl pointer-events-none`} />
                  )}
                  
                  <Icon className={`h-5 w-5 flex-shrink-0 relative z-10 ${!active && 'group-hover:scale-110 transition-transform duration-300'}`} />
                  {isOpen && (
                    <>
                      <span className="font-medium text-sm whitespace-nowrap relative z-10 flex-1">{item.label}</span>
                      {/* Count Badge */}
                      {item.count !== null && item.count > 0 && (
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full relative z-10 ${
                          active 
                            ? 'bg-white/20 text-white' 
                            : isDarkMode 
                              ? 'bg-[#38BDF8]/20 text-[#38BDF8]' 
                              : 'bg-[#38BDF8]/10 text-[#38BDF8]'
                        }`}>
                          {item.count}
                        </span>
                      )}
                      {/* Pending Badge (orange) */}
                      {item.badge && item.badge.count > 0 && (
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full relative z-10 ${item.badge.color} text-white`}>
                          {item.badge.count}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-3.5 rounded-xl transition-all duration-300 mt-4 group ${
              isDarkMode
                ? 'text-red-400 hover:bg-red-500/20 hover:text-red-300'
                : 'text-red-500 hover:bg-red-50 hover:text-red-600'
            } ${!isOpen && 'lg:justify-center lg:px-0'}`}
            data-testid="sidebar-logout"
          >
            <LogOut className="h-5 w-5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
            {isOpen && (
              <span className="font-medium text-sm whitespace-nowrap">
                {language === 'ar' ? 'تسجيل الخروج' : 'Logout'}
              </span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
